import { invoke } from '@shared/easyfy.decorators';

const TIMER_SELECTOR = '[data-container="countdown-timer"]';
const VALUE_SELECTOR = '[data-container="countdown-timer-value"]';
const VALUE_PAD_SELECTOR = '[data-container="countdown-timer-value-pad"]';
const REMAINING_SELECTOR = '[data-container="countdown-timer-remaining"]';

export type CountdownUnit = 'days' | 'hours' | 'minutes' | 'seconds';

const SECONDS_PER_UNIT: Record<CountdownUnit, number> = {
  days: 86400,
  hours: 3600,
  minutes: 60,
  seconds: 1,
};

interface Countdown {
  targetMs: number;
  unitKeys: CountdownUnit[];
  // Edited in place on each tick so a screen reader's virtual cursor keeps its place.
  valueNodes: Text[];
  // Padding zero kept out of the accessibility tree by the markup; null if not rendered.
  padNodes: (Text | null)[];
  unitElements: (HTMLElement | null)[];
  remainingNode: Text | null;
  // Read from root data attrs, not remainingNode, since a finished server render overwrites it.
  remainingText: string;
  finishedText: string;
  stepMs: number;
  values: number[];
  finished: boolean;
}

// Value digits are the last child, after any padding zero; add one if missing.
const textNodeOf = (element: HTMLElement): Text => {
  const last = element.lastChild;

  if (last !== null && last.nodeType === Node.TEXT_NODE) return last as Text;

  const node = document.createTextNode('');

  element.appendChild(node);

  return node;
};

// Mirrors CountdownTimerComponent.CalculateUnitValues on the server; writes into caller's buffer to avoid allocating.
export const writeUnitValues = (remainingSeconds: number, units: CountdownUnit[], into: number[]): number[] => {
  let rest = remainingSeconds > 0 ? Math.floor(remainingSeconds) : 0;

  for (let i = 0; i < units.length; i++) {
    const unitSeconds = SECONDS_PER_UNIT[units[i]];
    const value = Math.floor(rest / unitSeconds);
    rest -= value * unitSeconds;
    into[i] = value;
  }

  return into;
};

@invoke
export default class CountdownTimerComponent {
  static Instance: CountdownTimerComponent;

  private static countdowns: Countdown[] = [];
  private static timeoutId: number | null = null;
  private static listening = false;

  onInit() {
    CountdownTimerComponent.refreshComponent();

    if (!CountdownTimerComponent.listening) {
      // Resync on tab focus/bfcache restore, since a throttled hidden tab's pending tick goes stale.
      document.addEventListener('visibilitychange', CountdownTimerComponent.resyncOnVisible);
      CountdownTimerComponent.listening = true;
    }
  }

  private static resyncOnVisible = (): void => {
    if (document.visibilityState !== 'visible') return;

    CountdownTimerComponent.stop();
    CountdownTimerComponent.tick();
  };

  static refreshComponent = (): void => {
    CountdownTimerComponent.stop();

    CountdownTimerComponent.countdowns = Array.from(document.querySelectorAll<HTMLElement>(TIMER_SELECTOR))
      .map((root) => CountdownTimerComponent.read(root))
      .filter((countdown): countdown is Countdown => countdown !== null);

    if (CountdownTimerComponent.countdowns.length === 0) return;

    CountdownTimerComponent.tick();
  };

  static stop = (): void => {
    if (CountdownTimerComponent.timeoutId !== null) {
      window.clearTimeout(CountdownTimerComponent.timeoutId);
      CountdownTimerComponent.timeoutId = null;
    }
  };

  private static read(root: HTMLElement): Countdown | null {
    const targetMs = Date.parse(root.dataset.target ?? '');

    if (Number.isNaN(targetMs)) return null;

    const valueElements = Array.from(root.querySelectorAll<HTMLElement>(VALUE_SELECTOR)).filter((value) => (value.dataset.unit ?? '') in SECONDS_PER_UNIT);

    if (valueElements.length === 0) return null;

    const unitKeys = valueElements.map((value) => value.dataset.unit as CountdownUnit);
    const remaining = root.querySelector<HTMLElement>(REMAINING_SELECTOR);
    const remainingNode = remaining === null ? null : textNodeOf(remaining);

    return {
      targetMs,
      unitKeys,
      valueNodes: valueElements.map(textNodeOf),
      padNodes: valueElements.map((value) => {
        const pad = value.querySelector<HTMLElement>(VALUE_PAD_SELECTOR);

        return pad === null ? null : textNodeOf(pad);
      }),
      unitElements: valueElements.map((value) => value.parentElement),
      remainingNode,
      remainingText: root.dataset.remainingText ?? '',
      finishedText: root.dataset.finishedText ?? '',
      // Smallest unit's cadence catches every visible change.
      stepMs: Math.min(...unitKeys.map((unit) => SECONDS_PER_UNIT[unit])) * 1000,
      values: unitKeys.map(() => 0),
      finished: false,
    };
  }

  private static tick(): void {
    const now = Date.now();

    CountdownTimerComponent.timeoutId = null;

    CountdownTimerComponent.countdowns.forEach((countdown) => {
      if (countdown.finished) return;

      CountdownTimerComponent.render(countdown, now);
    });

    CountdownTimerComponent.schedule(now);
  }

  // Time until this countdown's smallest visible unit changes, or Infinity once finished.
  private static delayUntilChange(countdown: Countdown, nowMs: number): number {
    const remainingMs = countdown.targetMs - nowMs;

    if (remainingMs <= 0) return Infinity;

    // +1ms past the boundary, since exactly on it the value still floors to what's on screen.
    return (remainingMs % countdown.stepMs) + 1;
  }

  private static schedule(nowMs: number): void {
    const delay = CountdownTimerComponent.countdowns.reduce(
      (earliest, countdown) => Math.min(earliest, CountdownTimerComponent.delayUntilChange(countdown, nowMs)),
      Infinity
    );

    if (!Number.isFinite(delay)) return;

    CountdownTimerComponent.timeoutId = window.setTimeout(() => CountdownTimerComponent.tick(), delay);
  }

  private static render(countdown: Countdown, nowMs: number): void {
    const remainingSeconds = (countdown.targetMs - nowMs) / 1000;
    const isFinished = remainingSeconds <= 0;

    writeUnitValues(isFinished ? 0 : remainingSeconds, countdown.unitKeys, countdown.values);

    countdown.valueNodes.forEach((node, index) => {
      const value = countdown.values[index];
      const padNode = countdown.padNodes[index];
      const next = padNode === null ? String(value).padStart(2, '0') : String(value);

      // Skip no-op writes: replacing an unchanged text node drops a screen reader reading it.
      if (node.nodeValue !== next) node.nodeValue = next;

      if (padNode !== null) {
        const pad = value < 10 ? '0' : '';

        if (padNode.nodeValue !== pad) padNode.nodeValue = pad;
      }
    });

    if (countdown.remainingNode !== null) {
      const next = isFinished ? countdown.finishedText : countdown.remainingText;

      if (countdown.remainingNode.nodeValue !== next) countdown.remainingNode.nodeValue = next;
    }

    countdown.unitElements.forEach((element) => {
      if (element === null) return;

      if (isFinished) element.setAttribute('aria-hidden', 'true');
      else element.removeAttribute('aria-hidden');
    });

    countdown.finished = isFinished;
  }
}
